import { IAccount, IBranch, IUser } from './user'
import { IOrderedProduct, IProduct } from './products'
import { sumBy } from 'lodash/fp'

export type IOrderStatus =
  | 'open'
  | 'delayed'
  | 'approved'
  | 'requested'
  | 'rejected'
  | 'skipped'
  | 'submitted'
// replenish and fill_in are legacy types. Keep for backward compatibility
export type IOrderKind = 'replenish' | 'fill_in' | 'generic'

export type IOrderProcessingStatus =
  | 'none'
  | 'planned'
  | 'failed'
  | 'finished'
  | 'blocked'

export interface IOrder {
  id: number
  year: number
  month: number
  name: string
  kind: IOrderKind
  status: IOrderStatus
  processingStatus?: IOrderProcessingStatus
  blockedReason?: string
  navOrderId?: string
  purchaseOrderNumber?: string
  createdBy?: IUser
  updatedAt?: string
  updatedBy?: IUser
  requestedAt?: string
  requestedBy?: IUser
  rejectReason?: string
  approvedAt?: string
  approvedBy?: IUser
  submittedAt?: string
  submittedBy?: IUser
  autoSubmitted?: boolean
  dueDateAt?: string
  shipmentDateAt?: string
  approveDueDateAt?: string
  orderItems: IOrderItem[]
  erpCatalogName: string
  orderTrackingStatuses: IOrderTrackingStatus[]
  cost: number
}

export interface IOrderAccess {
  canRequest?: boolean
  canEdit?: boolean
  // the order is under a general manager's review: edit it only after an explicit confirmation
  editOnDemand?: boolean
  canReject?: boolean
  canApprove?: boolean
  canSubmit?: boolean
  approveText?: string
}

export type IPlannedOrder = IOrder & {
  kind: 'replenish'
  dueDateAt: string
  orderItems: IPlannedOrderItem[]
}

export type ICursoryOrder = IOrder & {
  orderItems?: IOrderItem[]
  branch: IBranch
  account: IAccount
}
export interface IDetailsOrder extends IOrder {
  branch: IBranch
  account: IAccount
  navCsv?: string
}

export interface IDetailsCart extends IOrder {
  branch: IBranch
  account: IAccount
  purchaseOrderNumber: string
  clearCartItems?: boolean
  moveSuggestedCartItemsToCart?: boolean
  navCsv?: string
}

export type IIdentifiableOrderAttrs = { id: number } & Partial<IOrder>

export type ISubmittedOrder = IOrder & {
  status: 'submitted'
  submittedAt: string
  submittedBy: IUser
  updatedAt: string
  updatedBy: IUser
}
export type IApprovedOrder = IOrder & {
  status: 'approved'
  approvedAt: string
  approvedBy: IUser
  updatedAt: string
  updatedBy: IUser
}

export type IStatusableOrder = Pick<
  IOrder,
  'kind' | 'status' | 'dueDateAt' | 'submittedAt'
>
export type IKindableOrder = Pick<IOrder, 'kind'>

// replenish and fill_in are legacy types. Keep for backward compatibility
export const isPlannedOrder = (
  order: Partial<IOrder>,
): order is IPlannedOrder => order.kind === 'replenish'

export const isEditableOrder = (order: Partial<IOrder>): boolean =>
  order.status === 'requested' || order.status === 'rejected'

export const isSubmittedOrder = (
  order: Partial<IOrder>,
): order is ISubmittedOrder => order.status === 'submitted'
export const isApprovedOrder = (
  order: Partial<IOrder>,
): order is IApprovedOrder => order.status === 'approved'
export const isRejectedOrder = (
  order: Partial<IOrder>,
): order is IApprovedOrder => order.status === 'rejected'
export const isRequestedOrder = (
  order: Partial<IOrder>,
): order is IApprovedOrder => order.status === 'requested'
export const isSkippedOrder = (
  order: Partial<IOrder>,
): order is IOrder & { status: 'skipped' } => order.status === 'skipped'
export const isDelayedOrder = (
  order: Partial<IOrder>,
): order is IOrder & { status: 'delayed' } => order.status === 'delayed'
export const isOpenOrder = (
  order: Partial<IOrder>,
): order is IOrder & { status: 'open' } => order.status === 'open'

export const fetchFlowAccess = (
  account: IAccount,
  user: IUser,
  order: IDetailsOrder,
): IOrderAccess => {
  const gmFlow = account.orderApproveFlow

  const isEmpty = sumBy('amountRequested', order.orderItems) === 0
  const isOwnOrder = !!order.createdBy && order.createdBy.id === user.id

  if (user.role === 'internal') {
    return {
      canEdit: ['open', 'delayed', 'requested'].includes(order.status),
    }
  }

  if (gmFlow && user.role === 'manager' && !user.generalManager) {
    // simple manager with gm ON

    if (order.status === 'open') {
      return {
        canRequest: !isEmpty,
        canEdit: true,
      }
    }

    if (order.status === 'delayed') {
      return {
        canRequest: !isEmpty,
        canEdit: true,
      }
    }

    if (order.status === 'requested') {
      return {
        canEdit: isOwnOrder,
        editOnDemand: true,
      }
    }

    if (order.status === 'rejected') {
      return {
        canEdit: isOwnOrder,
        editOnDemand: true,
        canRequest: isOwnOrder && !isEmpty,
      }
    }
  }

  if (gmFlow && user.role === 'manager' && user.generalManager) {
    // simple manager with gm OFF

    if (order.status === 'open') {
      return {
        canApprove: !isEmpty,
        canEdit: true,
        approveText: 'Place Order',
      }
    }

    if (order.status === 'delayed') {
      return {
        canApprove: !isEmpty,
        canEdit: true,
        approveText: 'Place Order',
      }
    }

    if (order.status === 'requested') {
      return {
        canEdit: true,
        canApprove: !isEmpty,
        canReject: !isEmpty,
        approveText: 'Approve',
      }
    }

    if (order.status === 'approved') {
      return {
        canEdit: true,
        canReject: true,
      }
    }

    if (order.status === 'rejected') {
      return {
        canEdit: true,
        canApprove: !isEmpty,
        approveText: 'Approve',
      }
    }
  }

  if (!gmFlow && user.role === 'manager') {
    // simple manager

    if (order.status === 'open') {
      return {
        canEdit: true,
        canSubmit: !isEmpty,
        approveText: 'Place Order',
      }
    }

    if (order.status === 'delayed') {
      return {
        canEdit: true,
        approveText: 'Place Order',
      }
    }
  }

  return {}
}

export const fetchFlowAccessForShoppingCart = (
  account: IAccount,
  user: IUser,
  branch: IBranch,
): IOrderAccess => {
  const gmFlow = account.orderApproveFlow

  if (gmFlow && user.role === 'manager' && !user.generalManager) {
    return {
      canEdit: true,
      canRequest: true,
    }
  }

  if (gmFlow && user.role === 'manager' && user.generalManager) {
    return {
      canEdit: true,
      canSubmit: true,
    }
  }

  if (!gmFlow && user.role === 'manager') {
    return {
      canEdit: true,
      canSubmit: true,
    }
  }

  return {}
}

export interface IOrderItem {
  id: number
  product: IOrderedProduct
  productId?: number
  updatedAt: string
  updatedBy: IUser
  price?: number
  unit?: string
  effectivePrice?: string
  effectiveUnit?: string

  amountRequested: number

  orderId?: number
  forecast?: IItemForecast
}

export interface ICartItem {
  id: number
  product: IOrderedProduct
  productId?: number
  updatedAt: string
  updatedBy: IUser
  unit?: string
  effectivePrice?: string
  effectiveUnit?: string
  suggestion?: boolean
  amountRequested: number
  amountPlanned: number
}

export interface IOrderTrackingStatus {
  id: number
  status?: string
  shippedAt?: string
  trackingNumber?: string
  shippingAgent?: string
  soNumber?: string
  trackingUrl?: string
}
export interface IPlannedOrderItem extends IOrderItem {
  forecast: IItemForecast
}

export interface IItemForecast {
  amountSuggested: number
  amountMinSuggested: number
  amountMaxSuggested: number
}

export interface IBranchForecast extends IItemForecast {
  id: number
  year: number
  month: number
  productId: number
}

export interface IBranchShipment {
  id: number
  date: string
  productId: number
  amount: number
}

export const isOrderItem = (
  itemOrProduct: Partial<IOrderItem | IProduct>,
): itemOrProduct is IOrderItem => !!(itemOrProduct as IOrderItem).product
